import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wrench, Eye, EyeSlash } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { homePath } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const LOGIN_BG =
  "https://images.unsplash.com/photo-1740209475472-aa7d280f7452?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxwcmVjaXNpb24lMjBjbmMlMjBtYWNoaW5pbmclMjB3b3Jrc2hvcHxlbnwwfHx8fDE3ODY1NjUzNDB8MA&ixlib=rb-4.1.0&q=85";

const ROLES = ["Manager", "Management User", "CNC Operator", "Manual Machinist", "Helper", "QC Inspector", "Delivery"];

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [form, setForm] = useState({
    identifier: "", password: "", name: "", email: "", mobile: "", role: "CNC Operator",
  });

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        const u = await login(form.identifier, form.password);
        navigate(homePath(u.role));
      } else if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email });
        setForgotSent(true);
      } else {
        const u = await register({
          name: form.name, email: form.email, mobile: form.mobile,
          password: form.password, role: form.role,
        });
        navigate(homePath(u.role));
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left hero */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={LOGIN_BG} alt="CNC machining" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/70" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2.5">
            <div className="rounded-md bg-white px-2 py-1.5">
              <img src="/apt-logo.jpg" alt="Apt Enterprises" className="h-5 w-auto" />
            </div>
            <span className="font-bold tracking-tight">Tool Room Workshop ERP</span>
          </div>
          <div>
            <h1 className="text-5xl font-bold tracking-tight leading-tight">Precision.<br />Tracked end&#8209;to&#8209;end.</h1>
            <p className="mt-4 text-slate-300 max-w-md">Manage jobs, machines, production logs, costs and utilization for your die, jig, fixture and mould workshop — all in one place.</p>
          </div>
          <div className="text-xs text-slate-400">CNC · EDM · Grinding · Inspection</div>
        </div>
      </div>

      {/* Right form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12 bg-slate-50">
        <div className="w-full max-w-md">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900">
            {mode === "login" ? "Welcome back" : mode === "forgot" ? "Reset your password" : "Create account"}
          </h2>
          <p className="text-slate-500 mt-1 mb-8">
            {mode === "login" ? "Sign in to your Apt workshop's dashboard." : mode === "forgot" ? "Enter your account email and we'll send you a reset link." : "Register a new workshop user."}
          </p>

          {mode === "forgot" && forgotSent ? (
            <div className="space-y-6">
              <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-4 py-3" data-testid="forgot-success">
                If an account exists for <span className="font-medium">{form.email}</span>, a password reset link has been sent. Check your inbox (and spam). The link expires in 30 minutes.
              </div>
              <button onClick={() => { setMode("login"); setForgotSent(false); setError(""); }} className="text-blue-600 font-medium hover:underline text-sm" data-testid="forgot-back-login">← Back to sign in</button>
            </div>
          ) : (
          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <>
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input id="name" className="mt-1.5 bg-white" value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="reg-name" />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" className="mt-1.5 bg-white" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="reg-email" />
                </div>
                <div>
                  <Label htmlFor="mobile">Mobile Number</Label>
                  <Input id="mobile" className="mt-1.5 bg-white" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} data-testid="reg-mobile" />
                </div>
                <div>
                  <Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => set("role", v)}>
                    <SelectTrigger className="mt-1.5 bg-white" data-testid="reg-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {mode === "login" && (
              <div>
                <Label htmlFor="identifier">Email or Mobile</Label>
                <Input id="identifier" className="mt-1.5 bg-white" value={form.identifier} onChange={(e) => set("identifier", e.target.value)} required data-testid="login-identifier" />
              </div>
            )}

            {mode === "forgot" && (
              <div>
                <Label htmlFor="forgot-email">Account Email</Label>
                <Input id="forgot-email" type="email" className="mt-1.5 bg-white" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="forgot-email" />
              </div>
            )}

            {mode !== "forgot" && (
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                {mode === "login" && (
                  <button type="button" onClick={() => { setMode("forgot"); setError(""); setForgotSent(false); }} className="text-xs text-blue-600 font-medium hover:underline" data-testid="forgot-password-link">Forgot password?</button>
                )}
              </div>
              <div className="relative mt-1.5">
                <Input id="password" type={showPw ? "text" : "password"} className="bg-white pr-10" value={form.password} onChange={(e) => set("password", e.target.value)} required data-testid={mode === "login" ? "login-password" : "reg-password"} />
                <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                  {showPw ? <EyeSlash className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            )}

            {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="auth-error">{error}</div>}

            <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 h-11" data-testid={mode === "login" ? "login-submit" : mode === "forgot" ? "forgot-submit" : "register-submit"}>
              {loading ? "Please wait..." : mode === "login" ? "Sign In" : mode === "forgot" ? "Send Reset Link" : "Create Account"}
            </Button>
          </form>
          )}

          <div className="mt-6 text-center text-sm text-slate-500">
            {mode === "login" ? (
              <>New here?{" "}
                <button onClick={() => { setMode("register"); setError(""); }} className="text-blue-600 font-medium hover:underline" data-testid="switch-register">Create an account</button>
              </>
            ) : mode === "forgot" ? (
              <>Remembered it?{" "}
                <button onClick={() => { setMode("login"); setError(""); setForgotSent(false); }} className="text-blue-600 font-medium hover:underline" data-testid="switch-login">Sign in</button>
              </>
            ) : (
              <>Already have an account?{" "}
                <button onClick={() => { setMode("login"); setError(""); }} className="text-blue-600 font-medium hover:underline" data-testid="switch-login">Sign in</button>
              </>
            )}
          </div>

          {mode === "login" && (
            <div className="mt-6 text-xs text-slate-400 text-center border-t border-slate-200 pt-4">
              Demo admin: <span className="font-mono">admin@toolroom.com</span> / <span className="font-mono">admin123</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
